"use client";

import React, {
  useMemo,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { usePredios } from "@/hooks/usePredios";
import { FaExclamationCircle } from "react-icons/fa";
import { TbMapCheck } from "react-icons/tb";
import { formatCadenaPredio } from "@/utils/format";
import Map from "../Map";
import { MapDrawer } from "@/components/Map/components/drawer/MapDrawer";
import CollapsibleCard from "../Map/components/collapsibleCard";
import useLayer from "../Map/hooks/useLayer";
import useMapControl from "../Map/hooks/useMapControl";
import useLocation from "../Map/hooks/useLocation";
import { FaMapLocation } from "react-icons/fa6";
import { useAuth } from "@/contexts/AuthContext";
import { toTitleCase } from "@/utils/strings";

/**
 * Selector de predios con visualización en mapa interactivo.
 *
 * Permite:
 * - Buscar predios por clave predial.
 * - Seleccionar predios desde el mapa o drawer lateral.
 * - Mostrar predios cercanos al hacer click en el mapa.
 * - Agregar elementos personalizados al drawer.
 * - Filtrar o decorar predios mediante tags, disabled, etc.
 *
 * @component
 *
 * @param {Object} props
 * @param {Object|null} props.value
 * Predio seleccionado actualmente.
 *
 * @param {(predio: Object|null) => void} props.onChange
 * Callback ejecutado al seleccionar un predio.
 *
 * @param {React.RefObject} props.mapRef
 * Referencia del componente Map.
 *
 * @param {boolean} [props.enableSearchClickMap=true]
 * Permite buscar predios cercanos al hacer click sobre el mapa.
 *
 * @param {boolean} [props.enableSearchMap=true]
 * Habilita el buscador de clave predial en el mapa.
 *
 * @param {boolean} [props.enableDrawerMap=true]
 * Habilita el drawer lateral de predios.
 *
 * @param {boolean} [props.enableMoveMap=false]
 * Permite mover libremente el mapa.
 *
 *  * @param {string} [props.baseLayerMode="default"]
 * Modo de visualización de la capa base.
 *
 * Opciones disponibles:
 * - default
 * - full
 *
 * @param {string} [props.baseLayerId="satellite-labels"]
 * Capa base inicial del mapa.
 *
 * Opciones disponibles:
 * - Default mode: "streets" , "satellite-labels"
 * - Full mode: "streets" , "satellite-labels", "satellite", "nav-day", "nav-night"
 *
 * @param {Array} [props.adittionalLayers=[]]
 * Capas adicionales a renderizar en el mapa.
 *
 * @param {(predio:Object)=>boolean|Object} [props.filterPredio]
 * Permite filtrar o decorar predios antes de renderizarlos en el drawer.
 *
 * Retornos soportados:
 *
 * false => Oculta el predio.
 *
 * true => Muestra el predio sin modificaciones.
 *
 * Object => Permite extender configuración visual.
 *
 * Ejemplo:
 *
 * filterPredio={(predio) => ({
 *   tag: "Con deuda",
 *   disabled: true,
 * })}
 *
 * @param {(items:Array)=>Array} [props.drawerItemsBuilder]
 * Permite modificar completamente los items renderizados en el drawer.
 *
 * Soporta:
 * - agregar items custom
 * - reordenar
 * - agrupar
 * - eliminar duplicados
 * - insertar predios manualmente
 *
 * Ejemplo:
 *
 * drawerItemsBuilder={(items) => [
 *   ...items,
 *   {
 *     type: "custom",
 *     id: "manual",
 *     title: "Ubicación Manual",
 *     content: <ManualSelector />,
 *   },
 * ]}
 *
 * Los items de tipo predio soportan:
 *
 * {
 *   type: "predio",
 *   predio,
 *   tag,
 *   disabled,
 * }
 *
 * /**
 * @param {Array|Function} [props.customDrawerItems=[]]
 *
 * Permite agregar elementos personalizados al drawer.
 *
 * Puede recibir:
 *
 * - Un array de items.
 * - Una función que recibe los predios cargados y retorna un array.
 *
 * Ejemplo:
 *
 * customDrawerItems={[
 *   {
 *     type: "custom",
 *     id: "manual",
 *     title: "Ubicación Manual",
 *     content: <ManualSelector />,
 *   },
 * ]}
 *
 * Tipos soportados:
 *
 * ----------------------------------------
 * CUSTOM
 * ----------------------------------------
 *
 * Permite controlar completamente el contenido
 * de la tarjeta.
 *
 * {
 *   type: "custom",
 *   id: "manual",
 *   title: "Ubicación Manual",
 *   content: <ManualSelector />,
 * }
 *
 * ----------------------------------------
 * SHAPE
 * ----------------------------------------
 *
 * Permite visualizar cualquier geometría
 * (GeoJSON, Feature, FeatureCollection, etc.)
 * reutilizando la apariencia estándar del drawer.
 *
 * {
 *   type: "shape",
 *   id: "fraccion-1",
 *   title: "Fracción N° 1",
 *   shape,
 * }
 *
 * Propiedades opcionales:
 *
 * {
 *   type: "shape",
 *   id: "fraccion-1",
 *   title: "Fracción N° 1",
 *   shape,
 *
 *   // Etiqueta visual
 *   tag: "250 m²",
 *
 *   // Deshabilita selección
 *   disabled: true,
 *
 *   // Contenido entre el título y el botón
 *   body: (
 *     <>
 *       <p>Área: 250 m²</p>
 *       <p>Uso: Residencial</p>
 *     </>
 *   ),
 *
 *   // Oculta el botón Visualizar
 *   showButton: false,
 *
 *   // Indica si muestra el shape automaticamente
 *  autoSelect: true, // default
 *
 *  //Cambia el texto del boton
 *  labelButton: "Visualizar"
 *
 *   // Reemplaza completamente el contenido
 *   content: <CustomContent />,
 *
 *   // Acción personalizada al seleccionar
 *   onSelect: (shape) => {},
 * }
 *
 * Prioridad de renderizado:
 *
 * content
 *   ↓
 * body + botón por defecto
 *   ↓
 * botón por defecto
 *
 * @param {string} [props.titleDrawer="Mis Predios"]
 * Título del drawer lateral.
 * @param {string} [props.labelOpenDrawer="Ver Predios"]
 * Etiqueta del botón para abrir el drawer.
 * @returns {JSX.Element}
 */
const PredioSelector = forwardRef(function PredioSelector(
  {
    value,
    onChange,
    mapRef,
    enableSearchClickMap = true,
    enableSearchMap = true,
    enableDrawerMap = true,
    enableMoveMap = false,
    baseLayerId = "satellite-labels",
    baseLayerMode = "default",
    adittionalLayers = [],
    filterPredio,
    drawerItemsBuilder,
    customDrawerItems = [],
    titleDrawer = "Mis Predios",
    labelOpenDrawer = "Ver Predios",
  },
  ref,
) {
  const { predios, isLoading, buscarPredios, buscarPrediosCercanos } =
    usePredios();

  const { addLayer, removeLayer } = useLayer(mapRef);

  const {
    fitBounds,
    addMapSearch,
    showLoading,
    hideLoading,
    addMapButtons,
    lockMap,
    showToast,
  } = useMapControl(mapRef);

  const { enableLiveLocation, centerMapOnUser } = useLocation(mapRef);

  const [selectedId, setSelectedId] = useState(value?.clave ?? null);
  const { user } = useAuth();

  // ✅ expone un ref “seguro” para RHF o para consumidores externos
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        // si quieres enfocar algo real, aquí podrías abrir el drawer o enfocar un input si existiera
      },
      getValue: () => value ?? null,
    }),
    [value],
  );

  const emitChange = useCallback(
    (predio) => {
      setSelectedId(predio?.clave ?? null);
      const selectedPredio = {
        ...predio,
        clave: formatCadenaPredio(predio.clave),
      };
      onChange?.(selectedPredio);
    },
    [onChange],
  );

  const buildPopup = useCallback(
    (boundary, moreInfo = true, showButton = true) => {
      return (
        <div style={{ width: "250px" }}>
          <div className="mb-2 border-b pb-1 text-center font-bold">
            🗒️ Información del Predio
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-1">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700">
                Clave Predial
              </label>
              <label className="block truncate text-xs text-gray-700">
                {boundary.clave}
              </label>
            </div>

            {moreInfo && boundary?.propietario && (
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700">
                  Propietario
                </label>
                <label className="block truncate text-xs text-gray-700">
                  {boundary?.propietario}
                </label>
              </div>
            )}

            {moreInfo && boundary?.cedularuc && (
              <div>
                <label className="block text-xs font-semibold text-gray-700">
                  Cédula / RUC
                </label>
                <label className="block truncate text-xs text-gray-700">
                  {boundary.cedularuc}
                </label>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-700">
                Dirección
              </label>
              <label className="block truncate text-xs text-gray-700">
                {boundary?.Direccion}
              </label>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700">
                Parroquia
              </label>
              <label className="block truncate text-xs text-gray-700">
                {boundary?.nombreparroquia}
              </label>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700">
                Barrio
              </label>
              <label className="block truncate text-xs text-gray-700">
                {boundary.nombrebarrio}
              </label>
            </div>

            {moreInfo && boundary?.email && (
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700">
                  Email
                </label>
                <label className="block truncate text-xs text-gray-700">
                  {boundary?.email}
                </label>
              </div>
            )}

            {moreInfo && boundary?.telefono && (
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-700">
                  Teléfono
                </label>
                <label className="block truncate text-xs text-gray-700">
                  {boundary?.telefono}
                </label>
              </div>
            )}
          </div>

          {showButton && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                className="btn"
                onClick={() => emitChange(boundary)}
              >
                <TbMapCheck /> Seleccionar
              </button>
            </div>
          )}
        </div>
      );
    },
    [emitChange],
  );

  const renderPredioOnMap = useCallback(
    (predio, { center = true } = {}) => {
      removeLayer();

      const [result] = addLayer({
        source: predio,
        visible: true,
        isUTM: true,
        popupContent: (_, data) => buildPopup(data, false),
        autoOpenPopup: false,
      });
      if (center && result?.bounds) fitBounds(result.bounds);
      setSelectedId(predio.clave);
      setTimeout(() => {
        result?.openPopup();
      }, 50);
    },
    [removeLayer, addLayer, buildPopup, fitBounds],
  );

  const searchPredio = useCallback(
    async (query) => {
      try {
        showLoading("Cargando predios...");
        const predio = await buscarPredios(formatCadenaPredio(query));

        if (!predio) {
          showToast("No se encontraron predios con esa clave", {
            type: "error",
          });
          return;
        }
        renderPredioOnMap(predio);
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, buscarPredios, renderPredioOnMap, showToast],
  );

  const enableLocation = useCallback(async () => {
    try {
      await enableLiveLocation();
    } catch (error) {
      console.error("Error al obtener ubicación:", error);
      showToast("No se pudo iniciar la geolocalización.", {
        type: "error",
      });
    }
  }, [enableLiveLocation, showToast]);

  const onReady = useCallback(async () => {
    try {
      showLoading("Obteniendo ubicación...");
      await enableLocation();
      if (enableSearchMap) {
        addMapSearch({
          placeholder: "Buscar por clave predial",
          title: "Buscar",
          onSearch: searchPredio,
        });
      }

      showLoading("Cargando componentes ...");
      addMapButtons({
        id: "btn-location",
        name: "Ubicar",
        icon: <FaMapLocation />,
        action: () => centerMapOnUser(),
      });

      showLoading("Agregando capas ...");
      if (value) renderPredioOnMap(value);
      else centerMapOnUser();

      if (!enableMoveMap) lockMap();
    } finally {
      hideLoading();
    }
  }, [
    showLoading,
    hideLoading,
    enableLocation,
    enableSearchMap,
    addMapSearch,
    searchPredio,
    addMapButtons,
    centerMapOnUser,
    value,
    renderPredioOnMap,
    enableMoveMap,
    lockMap,
  ]);

  const handleSelectShape = useCallback(
    (item) => {
      removeLayer();

      const [result] = addLayer({
        source: item.shape,
        visible: true,
        isUTM: true,
      });

      if (result?.bounds) {
        fitBounds(result.bounds);
      }
      setSelectedId(item.id);
    },
    [removeLayer, addLayer, fitBounds],
  );

  const handleSelectFromList = useCallback(
    (predio) => {
      if (predio?.error) return;
      renderPredioOnMap(predio);

      emitChange(predio);
    },
    [renderPredioOnMap, emitChange],
  );

  const onClickMap = useCallback(
    async (lat, lng) => {
      if (!enableSearchClickMap) return;

      try {
        showLoading("Buscando predios cercanos...");
        removeLayer();

        const data = await buscarPrediosCercanos(lat, lng, 100);

        addLayer({
          source: data,
          visible: true,
          isUTM: true,
          popupContent: (_, d) => buildPopup(d, false),
        });
      } finally {
        hideLoading();
      }
    },
    [
      enableSearchClickMap,
      showLoading,
      hideLoading,
      removeLayer,
      buscarPrediosCercanos,
      addLayer,
      buildPopup,
    ],
  );

  const processedPredios = useMemo(() => {
    return predios
      .map((predio) => {
        const result = filterPredio?.(predio);

        if (result === false) return null;

        if (result === true || result == null) {
          return {
            type: "predio",
            predio,
          };
        }

        return {
          type: "predio",
          predio,
          ...result,
        };
      })
      .filter(Boolean);
  }, [predios, filterPredio]);

  const resolvedCustomItems = useMemo(() => {
    if (typeof customDrawerItems === "function") {
      return customDrawerItems();
    }

    return customDrawerItems ?? [];
  }, [customDrawerItems]);

  const drawerItems = useMemo(() => {
    const baseItems = drawerItemsBuilder
      ? drawerItemsBuilder(processedPredios)
      : processedPredios;

    return [...baseItems, ...resolvedCustomItems];
  }, [processedPredios, drawerItemsBuilder, resolvedCustomItems]);

  const renderDrawerItem = useCallback(
    (item) => {
      if (item.type === "shape") {
        return (
          <CollapsibleCard
            key={item.id}
            isActive={selectedId == item.id}
            title={
              <div className="flex items-center justify-between gap-2">
                <span>{item.title}</span>

                {item.tag && (
                  <span className="rounded bg-yellow-500 px-2 py-1 text-xs text-white">
                    {item.tag}
                  </span>
                )}
              </div>
            }
            onClick={() => {
              if (!item.disabled) {
                if (item.disabled) return;

                if (item.autoSelect !== false) {
                  handleSelectShape(item);
                }

                item.onSelect?.(item);
              }
            }}
            content={
              item.content ?? (
                <>
                  {item.body}

                  {item.showButton !== false && (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        className="btn"
                        disabled={item.disabled}
                        onClick={(e) => {
                          e.stopPropagation();

                          if (item.disabled) return;

                          if (item.autoSelect !== false) {
                            handleSelectShape(item);
                          }

                          item.onSelect?.(item);
                        }}
                      >
                        <TbMapCheck /> {item.labelButton ?? "Visualizar"}
                      </button>
                    </div>
                  )}
                </>
              )
            }
          />
        );
      }
      if (item.type === "custom") {
        return (
          <CollapsibleCard
            key={item.id}
            title={item.title}
            content={item.content}
          />
        );
      }

      if (item.type === "predio") {
        const shape = item.predio;

        return (
          <CollapsibleCard
            key={item.id ?? shape.clave}
            title={
              <div className="flex items-center justify-between gap-2">
                <span>{item.title ?? `Clave Predial ${shape.clave}`}</span>

                {item.tag && (
                  <span className="rounded bg-yellow-500 px-2 py-1 text-xs text-white">
                    {item.tag}
                  </span>
                )}
              </div>
            }
            isActive={selectedId === shape.clave}
            onClick={() => {
              if (!item.disabled) {
                (item.onSelect ?? handleSelectFromList)(shape);
              }
            }}
            content={
              item.content ?? (
                <>
                  {item.body ?? (
                    <>
                      <p>
                        <strong>Parroquia:</strong>{" "}
                        {toTitleCase(shape.nombreparroquia)}
                      </p>

                      <p>
                        <strong>Barrio:</strong>{" "}
                        {toTitleCase(shape.nombrebarrio)}
                      </p>

                      <p>
                        <strong>Dirección:</strong>{" "}
                        {toTitleCase(shape.Direccion)}
                      </p>
                    </>
                  )}

                  {item.showButton !== false && (
                    <div className="flex justify-center pt-2">
                      <button
                        type="button"
                        className="btn"
                        disabled={item.disabled}
                        onClick={(e) => {
                          e.stopPropagation();

                          (item.onSelect ?? handleSelectFromList)(shape);
                        }}
                      >
                        <TbMapCheck /> Seleccionar
                      </button>
                    </div>
                  )}
                </>
              )
            }
          />
        );
      }

      return null;
    },
    [selectedId, handleSelectFromList, handleSelectShape],
  );

  const drawerContent = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex h-full flex-col items-center justify-center text-white">
          <div className="mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-t-2 border-white" />
          Cargando ...
        </div>
      );
    }

    if (!predios.length) {
      return (
        <div className="flex h-full flex-col items-center justify-center text-center text-white">
          <FaExclamationCircle size={36} className="mb-4" />
          No se han encontrado registros
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="text-lg font-bold text-white">{titleDrawer}</div>

        {drawerItems.map(renderDrawerItem)}
      </div>
    );
  }, [drawerItems, predios, isLoading, titleDrawer, renderDrawerItem]);

  return (
    <Map
      ref={mapRef}
      onReady={onReady}
      onClick={onClickMap}
      heightPreset="page"
      baseLayerId={baseLayerId}
      baseLayerMode={baseLayerMode}
      layers={adittionalLayers}
    >
      {enableDrawerMap && user && (
        <MapDrawer openButtonLabel={labelOpenDrawer}>{drawerContent}</MapDrawer>
      )}
    </Map>
  );
});

export default PredioSelector;
